import {useCallback, useEffect, useMemo, useState} from 'react'

import {getEvents} from '../../../containers/BuildsFetcher'
import {useBuildTypeData} from '../../../hooks/useEntity'
import {useThrottle} from '../../../hooks/useThrottle'
import {restApi} from '../../../services/rest'
import type {BuildId, MatrixParameter, NormalizedMatrixDependency} from '../../../types'
import {emptyArray} from '../../../utils/empty'
import type {Unsubscribe} from '../../../utils/subscriber'
import {subscribeOnBuildTypeEvents} from '../../../utils/subscriber'
import {BuildState} from '../../common/BuildStateFilter/BuildStateFilter.constants'

import type {MatrixBuildFilter} from './MatrixBuild.types'
import {getMatrixBuildArg} from './MatrixBuild.utils'

const THROTTLER_TIMEOUT = 2000

export const useQueryStateMatrixBuild = (buildId: BuildId) => {
  const arg = useMemo(() => getMatrixBuildArg(buildId), [buildId])
  return restApi.endpoints.getBuildNormalized.useQueryState(arg, {
    selectFromResult: ({data}) => {
      const build = data?.entities.builds?.[data?.result]
      return {
        dependencies:
          build?.matrixConfiguration?.dependencies?.dependency ??
          (emptyArray as NormalizedMatrixDependency[]),
        parameters:
          build?.matrixConfiguration?.parameters?.parameter ?? (emptyArray as MatrixParameter[]),
        buildsHash: data?.entities.builds,
        buildTypesHash: data?.entities.buildTypes,
        isReady: data != null,
      }
    },
  })
}

export const useMatrixBuildFilter = (): MatrixBuildFilter => {
  const [stateFilter, setStateFilter] = useState<BuildState>(BuildState.ALL)
  const [parametersFilter, setParametersFilter] = useState<Record<string, string[]> | null>(null)

  const onChangeStateFilter = useCallback((status: BuildState) => {
    setStateFilter(status)
    // setParametersFilter(null)
  }, [])

  const onChangeParametersFilter = useCallback(
    (name: string, values: string[]) => {
      setParametersFilter({
        ...(parametersFilter ?? {}),
        [name]: values,
      })
    },
    [parametersFilter],
  )

  return {
    stateFilter,
    parametersFilter,
    onChangeStateFilter,
    onChangeParametersFilter,
  }
}

export const useMatrixBuildSubsctiption = (buildId: BuildId) => {
  const arg = useMemo(() => getMatrixBuildArg(buildId), [buildId])
  const {refetch} = restApi.endpoints.getBuildNormalized.useQuery(arg)
  const {dependencies, buildsHash, buildTypesHash} = useQueryStateMatrixBuild(buildId)

  const fetch = useThrottle(refetch, THROTTLER_TIMEOUT)

  const internalId = useBuildTypeData({buildId}, buildType => buildType?.internalId)
  useEffect(
    () =>
      internalId != null
        ? subscribeOnBuildTypeEvents(internalId, getEvents(true, true), fetch)
        : undefined,
    [internalId, fetch],
  )

  useEffect(() => {
    const subsctiptions: Unsubscribe[] = []
    dependencies.forEach(dependency => {
      const build =
        dependency.build != null && buildsHash != null ? buildsHash[dependency.build] : null
      const buildType =
        build?.buildType != null && buildTypesHash != null ? buildTypesHash[build.buildType] : null

      if (buildType?.internalId != null && build?.state !== 'finished') {
        subsctiptions.push(
          subscribeOnBuildTypeEvents(buildType.internalId, getEvents(false, true), fetch),
        )
      }
    })

    return () => subsctiptions.forEach(unsubscribe => unsubscribe?.())
  }, [arg, dependencies, buildsHash, buildTypesHash, fetch])
}
